/**
 * API client. Token lives in localStorage (set once on the token gate);
 * every request carries `Authorization: Bearer <token>`. A 401 anywhere
 * clears back to the gate. In dev, Vite proxies /api → the dashboard-api
 * worker on :8789; in production the Pages app calls the worker URL set at
 * build time (VITE_API_BASE) or same-path /api behind a route.
 */

const TOKEN_KEY = "tp_dashboard_token";
// Production builds bake the API worker URL as a fallback so a deploy can
// never silently ship a dashboard calling same-origin /api on pages.dev
// (incident 2026-06-12: two builds without VITE_API_BASE broke every panel).
// Dev keeps BASE="" so the Vite proxy handles /api. VITE_API_BASE still wins
// when set (e.g. pointing a build at a staging worker).
const PROD_DEFAULT_API_BASE = "https://turboproc-worker-dashboard-api.dalehitt.workers.dev";
const BASE =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ??
  (import.meta.env.PROD ? PROD_DEFAULT_API_BASE : "");

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t.trim());
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${getToken() ?? ""}`,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; reason?: string };
      detail = j.reason ?? j.error ?? detail;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

/**
 * Multipart upload (file → Worker). No Content-Type header is set: the browser
 * adds `multipart/form-data; boundary=…` itself, which the Worker needs to
 * parse the body. Auth + error handling mirror `call`.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken() ?? ""}` },
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; reason?: string };
      detail = j.reason ?? j.error ?? detail;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => call<T>("GET", path),
  post: <T>(path: string, body: unknown) => call<T>("POST", path, body),
  patch: <T>(path: string, body: unknown) => call<T>("PATCH", path, body),
  del: <T>(path: string) => call<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => upload<T>(path, form),
};
